import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Skeleton,
  Alert,
  CircularProgress,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  AccountBalanceWallet as WalletIcon,
  TrendingUp as TrendingUpIcon,
  ShoppingCart as ShoppingCartIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material'
import { useUserDetails } from '../../hooks/useUserDetails'
import { formatDate, formatNumber, formatCurrency } from '../../utils/formatters'

const UserDetails = () => {
  const { userId } = useParams()
  const navigate = useNavigate()

  const { data: user, isLoading, error } = useUserDetails(userId)

  // Create full name from first_name and last_name
  const userName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email : ''
  const userInitials = userName ? userName.split(' ').map(n => n[0]).join('').toUpperCase() : '?'

  const handleGoBack = () => {
    navigate('/users')
  }

  // Loading state
  if (isLoading) {
    return (
      <Box>
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={handleGoBack} variant="outlined">
            Geri
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Kullanıcı Detayları
          </Typography>
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Skeleton variant="circular" width={80} height={80} sx={{ mx: 'auto', mb: 2 }} />
                <Skeleton variant="text" width="60%" sx={{ mx: 'auto', mb: 1 }} />
                <Skeleton variant="rectangular" width={60} height={24} sx={{ mx: 'auto', mb: 2, borderRadius: 2 }} />
                <Skeleton variant="text" width="80%" sx={{ mb: 1 }} />
                <Skeleton variant="text" width="70%" sx={{ mb: 1 }} />
                <Skeleton variant="text" width="60%" />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={8}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Card>
                  <CardContent>
                    <Skeleton variant="text" width="40%" height={40} />
                    <Skeleton variant="text" width="60%" />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card>
                  <CardContent>
                    <Skeleton variant="text" width="40%" height={40} />
                    <Skeleton variant="text" width="60%" />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    )
  }

  // Error state
  if (error) {
    return (
      <Box>
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={handleGoBack} variant="outlined">
            Geri
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Kullanıcı Detayları
          </Typography>
        </Box>
        <Alert severity="error">
          Kullanıcı bilgileri yüklenirken hata oluştu: {error.message}
        </Alert>
      </Box>
    )
  }

  // No user found
  if (!user) {
    return (
      <Box>
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={handleGoBack} variant="outlined">
            Geri
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Kullanıcı Detayları
          </Typography>
        </Box>
        <Alert severity="warning">
          Kullanıcı bulunamadı.
        </Alert>
      </Box>
    )
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success'
      case 'suspended': return 'warning'
      case 'banned': return 'error'
      default: return 'default'
    }
  }

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Aktif'
      case 'suspended': return 'Askıda'
      case 'banned': return 'Yasaklı'
      default: return status || 'Bilinmiyor'
    }
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleGoBack}
          variant="outlined"
        >
          Geri
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Kullanıcı Detayları
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* User Info Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar
                sx={{ width: 80, height: 80, mx: 'auto', mb: 2, bgcolor: 'primary.main' }}
              >
                {userInitials}
              </Avatar>

              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                {userName}
              </Typography>

              <Chip
                label={getStatusLabel(user.status)}
                color={getStatusColor(user.status)}
                sx={{ mb: 2 }}
              />

              <List>
                <ListItem>
                  <ListItemIcon>
                    <EmailIcon />
                  </ListItemIcon>
                  <ListItemText primary={user.email || '-'} />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <PhoneIcon />
                  </ListItemIcon>
                  <ListItemText primary={user.phone || '-'} />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <CalendarIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Kayıt Tarihi"
                    secondary={user.created_at ? formatDate(user.created_at, 'dd MMMM yyyy') : '-'}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Stats Cards */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <WalletIcon color="primary" />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {formatCurrency(user.credits || 0)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Mevcut Kredi
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TrendingUpIcon color="success" />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {formatNumber(user.total_transactions || 0)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Toplam İşlem
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <ShoppingCartIcon color="info" />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {formatNumber(user.total_rentals || 0)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Toplam Kiralama
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CheckCircleIcon color="success" />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {formatNumber(user.completed_rentals || 0)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Tamamlanan Kiralama
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Özet Bilgiler
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">Toplam Harcama</Typography>
                      <Typography variant="h6">{formatCurrency(user.total_spent || 0)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">Aktif Kiralama</Typography>
                      <Typography variant="h6">{formatNumber(user.active_rentals || 0)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">Son Aktivite</Typography>
                      <Typography variant="h6">
                        {user.last_login_at ? formatDate(user.last_login_at, 'dd/MM/yyyy') : '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">Email Durumu</Typography>
                      <Typography variant="h6">
                        {user.email_verified ? 'Doğrulandı' : 'Doğrulanmadı'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  )
}

export default UserDetails